import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { post } from "aws-amplify/api";

import { useAuthContext } from "@covertree/auth";
import { Toast } from "@covertree/utils";
import ForgotPasswordForm, {
  ForgotPasswordFormData,
} from "@covertree/ui/forms/auth/ForgotPasswordForm";
import { AUTH_ROUTES, BASE_AUTH_PATH } from "../paths";

const FETCH_ERROR_MESSAGE = "An error occurred. Please try again later.";

type ForgotPasswordProps = {
  useManualForgotPassword?: boolean;
};

export default function ForgotPassword({
  useManualForgotPassword = false,
}: ForgotPasswordProps) {
  const navigate = useNavigate();
  const { forgotPassword } = useAuthContext();

  const handleRedirectToCheckEmail = useCallback(
    (emailAddress: string) => {
      navigate(`/${BASE_AUTH_PATH}/${AUTH_ROUTES.CHECK_EMAIL}`, {
        state: { emailAddress },
      });
    },
    [navigate]
  );

  const handleRedirectToCreateNewPassword = useCallback(
    (emailAddress: string) => {
      navigate(`/${BASE_AUTH_PATH}/${AUTH_ROUTES.CREATE_NEW_PASSWORD}`, {
        state: { isPasswordReset: true, username: emailAddress },
      });
    },
    [navigate]
  );

  const manualForgotPassword = useCallback(
    async (emailAddress: string) => {
      try {
        const restOperation = post({
          apiName: "forgotPasswordApi",
          path: "/forgotPassword",
          options: { body: { emailAddress: emailAddress.toLowerCase() } },
        });

        const { body } = await restOperation.response;

        const data = (await body.json()) as {
          shouldRedirectToCheckEmail: boolean;
        };

        if (data?.shouldRedirectToCheckEmail) {
          handleRedirectToCheckEmail(emailAddress);
        } else {
          handleRedirectToCreateNewPassword(emailAddress);
        }
      } catch {
        Toast.showError(FETCH_ERROR_MESSAGE);
      }
    },
    [handleRedirectToCheckEmail, handleRedirectToCreateNewPassword]
  );

  const submit = useCallback(
    async ({ username }: ForgotPasswordFormData) => {
      if (useManualForgotPassword) {
        await manualForgotPassword(username);
      } else {
        await forgotPassword(username);
      }
    },
    [forgotPassword, manualForgotPassword, useManualForgotPassword]
  );

  return <ForgotPasswordForm usernameType="email" submit={submit} />;
}
